/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import useTabData from './useTabData';
import { TabLoading, TabError, TabEmpty } from './TabStates';
import { q, Kpi } from './kit';
import SubTabs from './SubTabs';
import DetailList from './DetailList';
import { formatUTCDate, formatLastActive } from '../../../utils/utils';

// No per-user bio-vitals summary endpoint exists — derive each vital's count from its
// per-type detail endpoint's totalItem (cheap: limit=1). Each vital is a sub-tab that
// shows this user's individual readings inline (mirrors the standalone <type>-details
// page: value + activity date, paginated), replacing the old redirect-on-click boxes.
const VITALS = [
  { key: 'bp', label: 'Blood pressure', ep: 'bp-details', valueKey: 'blood_pressure', valueLabel: 'Blood Pressure', dateKey: 'timestamp_utc', dateLabel: 'Datetime', dateFmt: 'utc' },
  { key: 'sugar', label: 'Blood sugar', ep: 'sugar-details', valueKey: 'sugar', valueLabel: 'Sugar', dateKey: 'timestamp_utc', dateLabel: 'Datetime', dateFmt: 'utc' },
  { key: 'oxygen', label: 'Oxygen (SpO₂)', ep: 'oxygen-details', valueKey: 'oxygen', valueLabel: 'Oxygen (%)', dateKey: 'timestamp_utc', dateLabel: 'Datetime', dateFmt: 'utc' },
  { key: 'salt', label: 'Salt intake', ep: 'salt-details', valueKey: 'salt_intake', valueLabel: 'Salt Intake (mg)', dateKey: 'created_at', dateLabel: 'Created At', dateFmt: 'last' },
  { key: 'water', label: 'Water intake', ep: 'water-details', valueKey: 'water_intake', valueLabel: 'Water Intake', dateKey: 'created_at', dateLabel: 'Created At', dateFmt: 'last' },
];

const dateFmt = (fmt) => (fmt === 'last' ? formatLastActive : formatUTCDate);

const BioVitalsTab = ({ userId, from, to, authGet }) => {
  const [sub, setSub] = useState(null);

  const { status, data, reload } = useTabData(async () => {
    const results = await Promise.all(
      VITALS.map((v) => authGet(`/admin/api/${v.ep}?${q(userId, from, to, { page: 1, limit: 1 })}`)),
    );
    const counts = {};
    VITALS.forEach((v, i) => { counts[v.key] = results[i].data.totalItem || 0; });
    return counts;
  }, [userId, from, to]);

  // Default the open sub-tab to the first populated vital once the counts arrive.
  useEffect(() => {
    if (status === 'ok' && data && sub == null) {
      const first = VITALS.find((v) => data[v.key] > 0) || VITALS[0];
      setSub(first.key);
    }
  }, [status, data, sub]);

  if (status === 'loading') return <TabLoading />;
  if (status === 'error') return <TabError onRetry={reload} />;

  const total = VITALS.reduce((s, v) => s + (data[v.key] || 0), 0);
  if (total === 0) {
    return <TabEmpty title="No bio-vital readings" message="This user has no blood-pressure, glucose, oxygen, salt or water readings in the selected window." icon="🩺" />;
  }

  const active = sub || (VITALS.find((v) => data[v.key] > 0) || VITALS[0]).key;
  const v = VITALS.find((x) => x.key === active) || VITALS[0];

  return (
    <div data-testid="ud-bio-vitals">
      <div className="ud-kpis ud-kpis--row">
        {VITALS.map((x) => (
          <Kpi key={x.key} label={x.label} value={data[x.key] || 0} sub="readings" />
        ))}
      </div>

      <SubTabs
        items={VITALS.map((x) => ({ key: x.key, label: x.label }))}
        active={active}
        onSelect={setSub}
        ariaLabel="Bio-vital type"
      />
      <DetailList
        key={active}
        userId={userId}
        from={from}
        to={to}
        authGet={authGet}
        endpoint={v.ep}
        testId={`ud-${v.key}-list`}
        columns={[
          { label: 'No', className: 'tnum', render: (r, i) => i + 1 },
          { label: v.valueLabel, className: 'tnum', render: (r) => (r[v.valueKey] != null && r[v.valueKey] !== '' ? r[v.valueKey] : '—') },
          { label: v.dateLabel, render: (r) => (r[v.dateKey] ? dateFmt(v.dateFmt)(r[v.dateKey]) : '—') },
        ]}
        empty={{ title: `No ${v.label.toLowerCase()} readings`, message: 'Nothing recorded for this user in the selected window.', icon: '🩺' }}
      />
    </div>
  );
};

export default BioVitalsTab;
